import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, FormikErrors } from 'formik';

import {
  isValidID,
  isValidRegistrationNumber,
  IsValidLulalendPassport,
} from 'src/helpers/validation';
import { FloatingInput, InfoTray } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { IMerchant } from 'src/common/types';

import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';

import './additional-fields-modal.scss';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { BCATopUpTealiumService } from 'src/containers/self-service/business-cash-advance/tagging/bca-top-up-tealium-service';

const TITLES = [
  { label: 'Mr', value: '1' },
  { label: 'Ms', value: '2' },
  { label: 'Mrs', value: '3' },
  { label: 'Miss', value: '4' },
];

const ID_TYPES = [
  { label: 'South African ID', value: '1' },
  { label: 'Passport', value: '2' },
];

const BUSINESS_TYPES = [
  { label: 'Private Company', value: '1' },
  { label: 'Sole Proprietorship', value: '2' },
  { label: 'Close Corporation', value: '3' },
  { label: 'Public Company', value: '4' },
];

type AdditionalFieldsForm = {
  title: string;
  name: string;
  surname: string;
  idType: string;
  passport?: string;
  id?: string;
  businessType: string;
  registrationNumber?: string;
};

type Option = {
  label: string;
  value: string;
};

export type AdditionalFieldsValues = {
  title?: Option;
  name: string;
  surname: string;
  idType?: Option;
  passport?: string;
  id?: string;
  businessType?: Option;
  registrationNumber?: string;
};

type FormikFormProps = {
  onNext: (values: AdditionalFieldsValues) => void;
  onHide: () => void;
  merchant: IMerchant;
  isBCATopUp?: boolean;
};

function FormikForm({ onNext, onHide, merchant, isBCATopUp }: FormikFormProps) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const [disableNext, setDisableNext] = useState<boolean>(true);

  const mapObjectsToValues = (values: AdditionalFieldsForm): AdditionalFieldsValues => ({
    ...values,
    title: TITLES.find((element) => element.label === values.title),
    idType: ID_TYPES.find((element) => element.label === values.idType),
    businessType: BUSINESS_TYPES.find((element) => element.label === values.businessType),
  });

  function validateForm(values: AdditionalFieldsForm) {
    const errors: FormikErrors<AdditionalFieldsForm> = {};

    const invalidTitle = !values.title;
    if (invalidTitle) {
      errors.title = 'Please select a title';
    }

    const invalidName = !values.name;
    if (invalidName) {
      errors.name = 'Please enter a name';
    }

    const invalidSurname = !values.surname;
    if (invalidSurname) {
      errors.surname = 'Please enter a surname';
    }

    const invalidIdType = !values.idType;
    if (invalidIdType) {
      errors.idType = 'Please select an identification type';
    }

    if (values.idType === 'Passport') {
      const invalidPassport = !IsValidLulalendPassport(values.passport);
      if (invalidPassport) {
        errors.passport = 'Please enter a valid passport number';
      }
    } else if (values.idType === 'South African ID') {
      const invalidId = !values.id || !isValidID(values.id);
      if (invalidId) {
        errors.id = 'Please enter a valid ID number';
      }
    }

    const invalidBusinessType = !values.businessType;
    if (invalidBusinessType) {
      errors.businessType = 'Please select a business type';
    }

    if (values.businessType !== '' && values.businessType !== 'Sole Proprietorship') {
      const invalidRegistrationNumber =
        !values.registrationNumber || !isValidRegistrationNumber(values.registrationNumber);
      if (invalidRegistrationNumber) {
        errors.registrationNumber = 'Please enter a valid registration number';
      }
    }

    setDisableNext(Object.keys(errors).length > 0);

    return errors;
  }

  return (
    <Formik<AdditionalFieldsForm>
      initialValues={{
        title: '',
        name: '',
        surname: '',
        idType: '',
        id: '',
        passport: '',
        businessType: '',
        registrationNumber: '',
      }}
      validate={validateForm}
      onSubmit={(values) => {
        TealiumEvents.businessInformationBCAClickNext(values.businessType, values.idType);
        onNext(mapObjectsToValues(values));
      }}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({ values }) => (
        <Form>
          <div className={'modal-card ' + (inMiniProgram ? 'keyboard-spacer' : '')}>
            <header className="modal-card-head is-hidden-mobile">
              <span
                className="is-icon-5 is-pulled-right close-button icon-cross is-clickable"
                onClick={() => onHide()}
              ></span>
              {isBCATopUp && (
                <div className="bca-topup-header">
                  <p className="modal-title">Your application is almost complete</p>
                  <p className="sub-text">We just need a few additional details from you</p>
                </div>
              )}
              {!isBCATopUp && <div className="modal-title">Hi {merchant?.companyName}</div>}
              {!isBCATopUp && (
                <div className="sub-text">
                  Your application is almost complete. We need just a few personal details from you
                  to proceed.
                </div>
              )}
            </header>
            <section className="modal-card-body">
              <div className="select-filter">
                <Field
                  name="title"
                  type="text"
                  placeholder="Title"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  items={TITLES}
                  getItemDisplayValue={(item) => item.label}
                  filterItemsWithInputValue={true}
                  fieldType="select"
                  isDark={false}
                />
              </div>

              <div className="select-filter">
                <Field
                  name="name"
                  type="text"
                  placeholder="Name"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                />
              </div>

              <div className="select-filter">
                <Field
                  name="surname"
                  type="text"
                  placeholder="Surname"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                />
              </div>

              <div className="select-filter">
                <Field
                  name="idType"
                  type="text"
                  placeholder="Type of identification"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  items={ID_TYPES}
                  getItemDisplayValue={(item) => item.label}
                  filterItemsWithInputValue={true}
                  fieldType="select"
                />
              </div>

              {!!values.idType && values.idType === 'South African ID' && (
                <div className="select-filter">
                  <Field
                    name="id"
                    type="text"
                    placeholder="ID"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                  />
                </div>
              )}
              {!!values.idType && values.idType === 'Passport' && (
                <div className="select-filter">
                  <Field
                    name="passport"
                    type="text"
                    placeholder="Passport number"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                  />
                </div>
              )}

              <div className="select-filter">
                <Field
                  name="businessType"
                  type="text"
                  placeholder="Type of business"
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  items={BUSINESS_TYPES}
                  getItemDisplayValue={(item) => item.label}
                  filterItemsWithInputValue={true}
                  fieldType="select"
                />
              </div>

              {!!values.businessType && values.businessType !== 'Sole Proprietorship' && (
                <div className="select-filter">
                  <Field
                    name="registrationNumber"
                    type="text"
                    placeholder="Business registration number"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                  />
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                type="submit"
                className="button is-primary"
                automationid="BCA_Button_Close_Modal"
                disabled={disableNext}
              >
                Next
              </button>
            </footer>
          </div>
        </Form>
      )}
    </Formik>
  );
}

type AdditionalFieldsModalProps = {
  onNext: (values: AdditionalFieldsValues) => void;
  show: boolean;
  onHide: () => void;
  isBCATopUp?: boolean;
};

export default function AdditionalFieldsModal({
  onNext,
  show,
  onHide,
  isBCATopUp,
}: AdditionalFieldsModalProps) {
  const { merchant } = useMerchantProfile();

  useEffect(() => {
    if (show && !isBCATopUp) {
      TealiumEvents.businessInformationBCAPageLoad();
    }

    if (isBCATopUp) {
      BCATopUpTealiumService.onShowAlmostCompleteModal();
    }
  }, [show, isBCATopUp]);

  return (
    <>
      <div className={`AdditionalFieldsModal modal ${show ? 'is-active' : ''} is-hidden-mobile`}>
        <div className="modal-background"></div>
        <FormikForm onNext={onNext} isBCATopUp={isBCATopUp} onHide={onHide} merchant={merchant} />
      </div>

      <div className={`AdditionalFieldsModal`} data-testid="transaction-view">
        <InfoTray
          selected={show}
          setSelected={() => onHide()}
          title={
            <>
              Hi {merchant?.companyName}
              <div className="sub-text">
                Your application is almost complete. We need just a few personal details from you to
                proceed.
              </div>
            </>
          }
          isFullscreen={true}
          className="is-hidden-tablet"
        >
          <p className="top-mobile-page-title navbar-title-mobile">Get Financed</p>
          <FormikForm onNext={onNext} isBCATopUp={isBCATopUp} onHide={onHide} merchant={merchant} />
        </InfoTray>
      </div>
    </>
  );
}
